import { useEffect, useState, useCallback, useMemo, memo, useRef, startTransition } from 'react';
import { fetchData } from '../../../apiService';
// Material UI imports
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  CircularProgress, Backdrop, Autocomplete, Checkbox, Chip, Collapse, InputAdornment,
  Pagination
} from '@mui/material';
import { Close, Add, Delete, Inventory2, Visibility, CheckCircle, Warning, Search, List, ExpandMore, ExpandLess, Refresh } from '@mui/icons-material';

// PROMAX PERFORMANCE: Web Worker for ultra-fast calculations
const createCalculationWorker = () => {
  const workerCode = `
    self.onmessage = function(e) {
      const { details } = e.data;
      
      // Helper function to round to 2 decimal places
      const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;
      
      // Ultra-fast calculation using optimized loops
      let totalQuantity = 0, totalAmount = 0, 
          totalTaxableAmount = 0, totalCGSTAmount = 0, totalSGSTAmount = 0, 
          totalIGSTAmount = 0, totalWithGST = 0;
      
      // Single loop optimization for maximum performance
      for (let i = 0; i < details.length; i++) {
        const d = details[i];
        const qty = parseFloat(d.Quantity) || 0;
        const amt = parseFloat(d.Amount) || 0;
        const taxableAmt = parseFloat(d.TaxableAmount) || 0;
        const cgstAmt = parseFloat(d.CGSTAmount) || 0;
        const sgstAmt = parseFloat(d.SGSTAmount) || 0;
        const igstAmt = parseFloat(d.IGSTAmount) || 0;
        const totalAmt = parseFloat(d.TotalAmount) || 0;
        
        totalQuantity += qty;
        totalAmount += amt;
        totalTaxableAmount += taxableAmt;
        totalCGSTAmount += cgstAmt;
        totalSGSTAmount += sgstAmt;
        totalIGSTAmount += igstAmt;
        totalWithGST += totalAmt;
      }

      // Calculate RoundOff and GrandTotal
      const calculatedTotal = roundTo2(totalWithGST);
      const roundedTotal = Math.round(calculatedTotal); // Round to nearest whole number
      const roundOff = roundTo2(roundedTotal - calculatedTotal);

      self.postMessage({
        TotalQuantity: roundTo2(totalQuantity),
        TotalAmount: roundTo2(totalAmount),
        TotalTaxableAmount: roundTo2(totalTaxableAmount),
        TotalCGSTAmount: roundTo2(totalCGSTAmount),
        TotalSGSTAmount: roundTo2(totalSGSTAmount),
        TotalIGSTAmount: roundTo2(totalIGSTAmount),
        TotalWithGST: calculatedTotal,
        RoundOff: roundOff,
        GrandTotal: roundedTotal
      });
    };
  `;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

const StockTransferOutModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  transferDetails,
  setTransferDetails,
  originalTransferDetails,
  onSave,
  items,
  branches,
  loading,
  companyId,
  branchId,
  token
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State declarations
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [itemsPerPage] = useState(50);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [transporterLoading, setTransporterLoading] = useState(false);

  // Store original transfer details for edit mode balance calculations
  // const [originalTransferDetails, setOriginalTransferDetails] = useState([]);
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Stock Transfer Out' : modalAction === 'edit' ? 'Edit Stock Transfer Out' : 'View Stock Transfer Out';

  // PROMAX PERFORMANCE: Web Worker for calculations
  const calculationWorkerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const calculationTimeoutRef = useRef(null);
  const transferDetailsRef = useRef(transferDetails);
  const formDataRef = useRef(formData);

  // Initialize Web Worker
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    if (!calculationWorkerRef.current) {
      calculationWorkerRef.current = createCalculationWorker();

      calculationWorkerRef.current.onmessage = (e) => {
        if (isMountedRef.current) {
          const calculatedTotals = e.data;
          startTransition(() => {
            setFormData(prev => ({ ...prev, ...calculatedTotals }));
          });
        }
      };
    }

    return () => {
      isMountedRef.current = false;
      if (calculationWorkerRef.current) {
        calculationWorkerRef.current.terminate();
        calculationWorkerRef.current = null;
      }
    };
  }, []);

  // Update refs when props change
  useEffect(() => {
    transferDetailsRef.current = transferDetails;
    formDataRef.current = formData;
  }, [transferDetails, formData]);

  // PROMAX PERFORMANCE: Web Worker-based calculations with zero main thread blocking
  const performCalculations = useCallback((details) => {
    if (!calculationWorkerRef.current || details.length === 0) {
      return;
    }

    // Send to Web Worker for non-blocking calculation
    calculationWorkerRef.current.postMessage({
      details
    });
  }, []);

  // Memoized set of already added item IDs for better performance
  const addedItemIds = useMemo(() => {
    if (!transferDetails || transferDetails.length === 0) return new Set();

    return new Set(transferDetails.map(detail => {
      const itemId = detail.ItemId || detail.Id;
      return parseInt(itemId);
    }).filter(id => !isNaN(id)));
  }, [transferDetails.length, transferDetails.map(d => d.ItemId || d.Id).join(',')]);

  // Check if item is already added to transfer (optimized with Set lookup)
  const isItemAlreadyInTransfer = useCallback((itemId) => {
    return addedItemIds.has(parseInt(itemId));
  }, [addedItemIds]);

  // Memoized items map for better performance
  const itemsMap = useMemo(() => {
    const map = new Map();
    items.forEach(item => {
      map.set(item.Id, item);
    });
    return map;
  }, [items]);

  // Fetch branch details for auto-populating transporter information
  const fetchBranchDetails = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      if (response.responseCode == '1111') {
        const branchData = response.response_data || {};
        return {
          branchName: branchData.Name || '',
          location: branchData.Location || '',
          addressLine1: branchData.AddressLine1 || '',
          addressLine2: branchData.AddressLine2 || '',
          pincode: branchData.PinCode || '',
          companyName: branchData.CompanyName || branchData.Name || '',
          gstNumber: branchData.GSTNumber || branchData.CompanyGSTNumber || ''
        };
      }
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '' };
    } catch (error) {
      console.error('Error fetching Branch Details:', error);
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '' };
    }
  };

  // Handle Get Transporter from API button click
  const handleGetTransporterFromAPI = async () => {
    if (!branchId || !token) {
      showError('Branch ID or token is missing');
      return;
    }

    setTransporterLoading(true);
    try {
      const branchDetails = await fetchBranchDetails();
      
      // Update transporter details with company information from API
      setFormData(prev => ({
        ...prev,
        TransporterName: branchDetails.companyName,
        TransporterGST: branchDetails.gstNumber
      }));

      // Mark as having unsaved changes
      setHasUnsavedChanges(true);

      // Show success message
      setSnackbar({ 
        open: true, 
        message: 'Transporter details updated from API successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error getting transporter details from API:', error);
      showError('Failed to get transporter details from API. Please try again.');
    } finally {
      setTransporterLoading(false);
    }
  };

  // Auto-populate transporter details when modal opens (for create mode)
  useEffect(() => {
    if (open && modalAction === 'create' && branchId && token) {
      const populateTransporterDetails = async () => {
        try {
          const branchDetails = await fetchBranchDetails();
          
          // Auto-populate transporter details with company information
          setFormData(prev => ({
            ...prev,
            TransporterName: branchDetails.companyName,
            TransporterGST: branchDetails.gstNumber
          }));
        } catch (error) {
          console.error('Error auto-populating transporter details:', error);
        }
      };

      populateTransporterDetails();
    }
  }, [open, modalAction, branchId, token]);

  // Style helpers
  const disabledFieldStyle = {
    '& .MuiInputBase-input': { color: '#6b7280', fontWeight: 500 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
  };

  const tinyInput = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'right' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
  };

  const tinyInputCenter = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'center' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.PurchaseRate || 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(si => si.Id !== item.Id));
    }
  };

  // Handle quantity/rate change for selected items
  const handleSelectedItemChange = (itemId, field, value) => {
    if (field === 'selectedQuantity') {
      const availableBalance = getAvailableBalance(itemId);
      const quantity = parseFloat(value) || 0;
      
      // Validate quantity against available balance (including original allocation in edit mode)
      if (quantity > availableBalance) {
        const item = items.find(i => i.Id === itemId);
        showError(`Quantity (${quantity}) cannot exceed available balance (${availableBalance}) for ${item?.Name || 'this item'}`);
        return; // Don't update if validation fails
      }
    }
    
    setSelectedItems(prev => prev.map(item => 
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Remove selected item
  const removeSelectedItem = (itemId) => {
    setSelectedItems(prev => prev.filter(item => item.Id !== itemId));
  };

  // Add selected items to transfer details
  const handleAddSelectedItems = useCallback(async () => {
    if (selectedItems.length === 0) return;

    // Validate all selected items against available balance before adding
    const validationErrors = [];
    selectedItems.forEach(selectedItem => {
      const availableBalance = getAvailableBalance(selectedItem.Id);
      const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
      
      if (quantity > availableBalance) {
        const item = items.find(i => i.Id === selectedItem.Id);
        validationErrors.push(`${item?.Name || 'Item'}: Quantity (${quantity}) exceeds available balance (${availableBalance})`);
      }
    });

    if (validationErrors.length > 0) {
      showError(`Cannot add items: ${validationErrors.join(', ')}`);
      return;
    }

    setAddingItemsLoading(true);
    
    try {
      // Process items in batches for better performance
      const processItemsInBatches = (items, batchSize = 50) => {
        return new Promise((resolve) => {
          const batches = [];
          for (let i = 0; i < items.length; i += batchSize) {
            batches.push(items.slice(i, i + batchSize));
          }

          let processedItems = [];
          let currentBatch = 0;

          const processBatch = () => {
            if (currentBatch >= batches.length) {
              resolve(processedItems);
              return;
            }

            const batch = batches[currentBatch];
            const batchResults = batch.map(selectedItem => {
              const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
              const rate = parseFloat(selectedItem.selectedRate) || parseFloat(selectedItem.PurchaseRate) || 0;
              const gstRate = selectedItem.GSTRate || 0;

              // Helper function to round to 2 decimal places
              const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

              const amount = roundTo2(quantity * rate);
              const gstAmount = roundTo2((amount * gstRate) / 100);
              const cgstAmount = roundTo2(gstAmount / 2);
              const sgstAmount = roundTo2(gstAmount / 2);
              const igstAmount = roundTo2(gstAmount);
              const totalAmount = roundTo2(amount + gstAmount);

              return {
                Id: 0,
                ItemId: selectedItem.Id,
                HSNCode: selectedItem.HSNCode || '',
                Quantity: quantity,
                Rate: rate,
                Amount: amount,
                GSTRate: gstRate,
                TaxableAmount: amount,
                CGSTAmount: cgstAmount,
                SGSTAmount: sgstAmount,
                IGSTAmount: igstAmount,
                TotalAmount: totalAmount,
                IsActive: true
              };
            });

            processedItems = [...processedItems, ...batchResults];
            currentBatch++;

            // Use requestIdleCallback for non-blocking processing
            if (window.requestIdleCallback) {
              requestIdleCallback(processBatch);
            } else {
              setTimeout(processBatch, 0);
            }
          };

          processBatch();
        });
      };

      const newItems = await processItemsInBatches(selectedItems);
      
      // Batch DOM update
      requestAnimationFrame(() => {
        const updatedDetails = [...transferDetails, ...newItems];
        setTransferDetails(updatedDetails);
        
        // Defer calculation to next frame
        requestAnimationFrame(() => {
          performCalculations(updatedDetails);
        });
      });

      // Clear selections and close modal
      setSelectedItems([]);
      setGetItemsModalOpen(false);
      setHasUnsavedChanges(true);
      
    } catch (error) {
      console.error('Error adding items:', error);
      showError('Failed to add selected items. Please try again.');
    } finally {
      setAddingItemsLoading(false);
    }
  }, [selectedItems, transferDetails, performCalculations]);

  // Open Get Items modal
  const handleOpenGetItemsModal = () => {
    setGetItemsModalOpen(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);
  };

  // Close Get Items modal
  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!getItemsSearchValue.trim()) return items;
    
    const searchLower = getItemsSearchValue.toLowerCase();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchLower) ||
      item.HSNCode?.toLowerCase().includes(searchLower) ||
      item.Description?.toLowerCase().includes(searchLower)
    );
  }, [items, getItemsSearchValue]);

  // Paginated items for Get Items modal
  const getPaginatedItems = useMemo(() => {
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  const totalItemsPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
    }
  }, [open, modalAction]);

  // Helper function to get available balance for an item (considering edit mode)
  const getAvailableBalance = useCallback((itemId, currentDetailIndex = null) => {
    const item = itemsMap.get(parseInt(itemId));
    if (!item) return 0;
    
    const currentBalance = item.BalanceStock || 0;
    
    // In edit mode, add back the original allocated quantity for this item
    if (modalAction === 'edit' && originalTransferDetails.length > 0) {
      const originalDetail = originalTransferDetails.find(d => parseInt(d.ItemId) === parseInt(itemId));
      const originalQuantity = originalDetail ? (parseFloat(originalDetail.Quantity) || 0) : 0;
      
      // If we're checking for a specific detail row, don't add back its own original quantity
      // This prevents double-counting when validating the same row
      if (currentDetailIndex !== null) {
        const currentDetail = transferDetails[currentDetailIndex];
        if (currentDetail && parseInt(currentDetail.ItemId) === parseInt(itemId)) {
          return currentBalance + originalQuantity;
        }
      }
      
      return currentBalance + originalQuantity;
    }
    
    return currentBalance;
  }, [itemsMap, modalAction, originalTransferDetails, transferDetails]);

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'StockTransferNumber':
        if (!value || value.toString().trim() === '') return 'Transfer Number is required';
        return '';
      case 'StockTransferDate':
        if (!value) return 'Transfer Date is required';
        return '';
      case 'ToBranchId':
        if (!value) return 'To Branch is required';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const StockTransferNumberError = validateField('StockTransferNumber', formData.StockTransferNumber);
    if (StockTransferNumberError) newErrors.StockTransferNumber = StockTransferNumberError;

    const StockTransferDateError = validateField('StockTransferDate', formData.StockTransferDate);
    if (StockTransferDateError) newErrors.StockTransferDate = StockTransferDateError;

    const toBranchError = validateField('ToBranchId', formData.ToBranchId);
    if (toBranchError) newErrors.ToBranchId = toBranchError;

    // Transfer details validation
    if (transferDetails.length === 0) {
      newErrors.transferDetails = 'At least one item is required';
    } else {
      const detailErrors = [];
      transferDetails.forEach((detail, index) => {
        const itemErrors = {};
        if (!detail.ItemId) itemErrors.ItemId = 'Item is required';
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.Quantity = 'Quantity must be greater than 0';
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.Rate = 'Rate must be greater than 0';
        }
        if (Object.keys(itemErrors).length > 0) {
          detailErrors[index] = itemErrors;
        }
      });
      if (detailErrors.length > 0) {
        newErrors.details = detailErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ StockTransferNumber: true, StockTransferDate: true, ToBranchId: true });

    // Build validation error messages
    const validationMessages = [];

    if (!formData.StockTransferNumber || formData.StockTransferNumber.toString().trim() === '') {
      validationMessages.push('Transfer Number is required');
    }
    if (!formData.StockTransferDate) {
      validationMessages.push('Transfer Date is required');
    }
    if (!formData.ToBranchId) {
      validationMessages.push('To Branch is required');
    }
    if (transferDetails.length === 0) {
      validationMessages.push('At least one item is required');
    } else {
      // Check each item for Quantity and Rate
      const itemErrors = [];
      transferDetails.forEach((detail, index) => {
        if (!detail.ItemId) {
          itemErrors.push(`Row ${index + 1}: Item is required`);
        }
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.push(`Row ${index + 1}: Quantity is required`);
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.push(`Row ${index + 1}: Rate is required`);
        }
      });
      if (itemErrors.length > 0) {
        validationMessages.push(...itemErrors);
      }
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDetailChange = (index, field, value) => {
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedDetails = [...transferDetails];

    // Handle ItemId change - auto-update HSN code and rate
    if (field === 'ItemId') {
      const selectedItem = itemsMap.get(parseInt(value));
      if (selectedItem) {
        updatedDetails[index] = { 
          ...updatedDetails[index], 
          ItemId: value,
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: selectedItem.GSTRate || 0,
          Rate: selectedItem.PurchaseRate || 0
        };
      } else {
        updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      }
    } else if (['Quantity', 'Rate'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setTransferDetails(updatedDetails);
        performCalculations(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
      
      // Validate quantity against available balance (considering edit mode)
      if (field === 'Quantity') {
        const currentDetail = updatedDetails[index];
        const availableBalance = getAvailableBalance(currentDetail.ItemId, index);
        if (numValue > availableBalance) {
          const item = itemsMap.get(parseInt(currentDetail.ItemId));
          showError(`Quantity (${numValue}) cannot exceed available balance (${availableBalance}) for ${item?.Name || 'this item'}`);
          return; // Don't update if validation fails
        }
      }
      
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    } else {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    }

    if (['Quantity', 'Rate', 'GSTRate', 'ItemId'].includes(field)) {
      const currentDetail = updatedDetails[index];
      const quantity = Math.max(0, parseFloat(currentDetail.Quantity) || 0);
      const rate = Math.max(0, parseFloat(currentDetail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(currentDetail.GSTRate) || 0);

      // Helper function to round to 2 decimal places
      const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

      // Calculate basic amount
      const amount = roundTo2(quantity * rate);

      // Calculate taxable amount (no discount)
      const taxableAmount = amount;
      
      // Calculate GST amounts
      const gstAmount = roundTo2((taxableAmount * gstRate) / 100);
      const cgstAmount = roundTo2(gstAmount / 2);
      const sgstAmount = roundTo2(gstAmount / 2);
      const igstAmount = roundTo2(gstAmount); // For IGST scenarios
      
      // Total amount including GST
      const totalAmount = roundTo2(taxableAmount + gstAmount);

      updatedDetails[index] = {
        ...updatedDetails[index],
        Amount: amount,
        TaxableAmount: taxableAmount,
        CGSTAmount: cgstAmount,
        SGSTAmount: sgstAmount,
        IGSTAmount: igstAmount,
        TotalAmount: totalAmount
      };
    }
    setTransferDetails(updatedDetails);
    performCalculations(updatedDetails);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = transferDetails.filter((_, i) => i !== index);
    setTransferDetails(updatedDetails);
    performCalculations(updatedDetails);
  };

  useEffect(() => {
    // Auto-calculate totals when details change
    if (!isViewMode && transferDetails.length > 0) {
      performCalculations(transferDetails);
    }
  }, [transferDetails.length, performCalculations]);

  // Handle View All Items modal opening
  const handleViewAllItemsOpen = () => {
    setItemsModalOpen(true);
  };

  const handleViewAllItemsClose = () => {
    setItemsModalOpen(false);
  };

  const compactInput = { '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' }, ...(isViewMode && disabledFieldStyle) };

  return (
    <Dialog open={open} onClose={(e, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        if (hasUnsavedChanges && !isViewMode) {
          setCancelConfirmOpen(true);
          return;
        }
      }
      onClose();
    }}
      maxWidth="xl" fullWidth fullScreen={isMobile} disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1400px', position: 'relative' } }}>

      <DialogTitle sx={{ py: 1, px: 2, borderBottom: isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe', bgcolor: isViewMode ? '#1e293b' : '#eff6ff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2 sx={{ color: isViewMode ? '#f8fafc' : '#2563eb', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isViewMode ? '#f8fafc' : '#1e3a8a', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
          </Box>
          <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5, color: isViewMode ? '#f8fafc' : 'inherit' }}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: isViewMode ? '#f8fafc' : '#fefffe' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#2563eb', mb: 1, display: 'block' }}>Transfer Information</Typography>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="Transfer No *"
              value={formData.StockTransferPrefix && formData.FinYear 
                ? `${formData.StockTransferPrefix}/${formData.StockTransferNumber}/${formData.FinYear}`
                : formData.StockTransferNumber || ''}
              onChange={(e) => handleChange('StockTransferNumber', e.target.value)}
              onBlur={() => handleBlur('StockTransferNumber')}
              disabled={isViewMode}
              error={touched.StockTransferNumber && !!errors.StockTransferNumber}
              helperText={touched.StockTransferNumber && errors.StockTransferNumber}
              sx={compactInput}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Transfer Date *"
              value={formData.StockTransferDate || ''}
              onChange={(e) => handleChange('StockTransferDate', e.target.value)}
              onBlur={() => handleBlur('StockTransferDate')}
              disabled={isViewMode}
              error={touched.StockTransferDate && !!errors.StockTransferDate}
              helperText={touched.StockTransferDate && errors.StockTransferDate}
              InputLabelProps={{ shrink: true }}
              sx={compactInput}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            {isViewMode ? (
              <TextField fullWidth size="small" label="To Branch" value={branches.find(b => b.Id === formData.ToBranchId)?.Name || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
            ) : (
              <FormControl fullWidth size="small" sx={compactInput} error={touched.ToBranchId && !!errors.ToBranchId}>
                <InputLabel>To Branch *</InputLabel>
                <Select
                  value={formData.ToBranchId || ''}
                  onChange={(e) => handleChange('ToBranchId', e.target.value)}
                  onBlur={() => handleBlur('ToBranchId')}
                  label="To Branch *"
                  sx={{ fontSize: '0.8rem' }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                >
                  {branches.filter(b => b.Id !== branchId).map(branch => (
                    <MenuItem key={branch.Id} value={branch.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                      {branch.Name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.ToBranchId && errors.ToBranchId && (
                  <FormHelperText>{errors.ToBranchId}</FormHelperText>
                )}
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              value={formData.Remarks || ''}
              onChange={(e) => handleChange('Remarks', e.target.value)}
              disabled={isViewMode}
              sx={compactInput}
            />
          </Grid>
        </Grid>

        {/* Transport Information Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#2563eb' }}>Transport Information</Typography>
          {/* {!isViewMode && (
            <Button
              size="small"
              onClick={handleGetTransporterFromAPI}
              disabled={transporterLoading || !branchId || !token}
              startIcon={transporterLoading ? <CircularProgress size={14} /> : <Refresh />}
              sx={{ 
                py: 0.25, 
                px: 1, 
                fontSize: '0.7rem', 
                minHeight: 0, 
                bgcolor: '#10b981', 
                color: 'white',
                '&:hover': { bgcolor: '#059669' },
                '&:disabled': { bgcolor: '#d1d5db', color: '#6b7280' }
              }}
            >
              {transporterLoading ? 'Loading...' : 'Get from API'}
            </Button>
          )} */}
        </Box>
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Transporter Name"
              value={formData.TransporterName || ''}
              onChange={(e) => handleChange('TransporterName', e.target.value)}
              disabled={isViewMode}
              sx={compactInput}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Transporter GST"
              value={formData.TransporterGST || ''}
              onChange={(e) => handleChange('TransporterGST', e.target.value)}
              disabled={isViewMode}
              sx={compactInput}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Vehicle Number"
              value={formData.VehicleNumber || ''}
              onChange={(e) => handleChange('VehicleNumber', e.target.value)}
              disabled={isViewMode}
              sx={compactInput}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Distance (KM)"
              value={formData.Distance || ''}
              onChange={(e) => handleChange('Distance', e.target.value)}
              disabled={isViewMode}
              inputProps={{ min: 0 }}
              sx={compactInput}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: errors.transferDetails ? '#dc2626' : (isViewMode ? '#475569' : '#2563eb') }}>Transfer Items *</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>({transferDetails.length} items)</Typography>
            {errors.transferDetails && (
              <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.7rem' }}>({errors.transferDetails})</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {transferDetails.length > 5 && (
              <Button
                size="small"
                onClick={handleViewAllItemsOpen}
                startIcon={<Visibility sx={{ fontSize: 14 }} />}
                sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e40af' }}
              >
                View All
              </Button>
            )}
            {!isViewMode && (
              <Button 
                size="small" 
                startIcon={<List sx={{ fontSize: 16 }} />} 
                onClick={handleOpenGetItemsModal} 
                variant="contained" 
                sx={{ 
                  py: 0.25, 
                  px: 1, 
                  fontSize: '0.7rem', 
                  minHeight: 0, 
                  bgcolor: '#1e40af', 
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                Get Items
              </Button>
            )}
          </Box>
        </Box>

        {/* Desktop Table View - Fixed height for 5 rows */}
        {!isMobile && (
          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 1.5, border: errors.transferDetails ? '1px solid #dc2626' : (isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe'), borderRadius: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: isViewMode ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '4%', background: 'transparent', color: 'white', fontWeight: 600 }}>Sl No</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #dbeafe', width: '22%', background: 'transparent', color: 'white', fontWeight: 600 }}>Item</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>HSN Code</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Balance</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Qty</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dbeafe', width: '10%', background: 'transparent', color: 'white', fontWeight: 600 }}>Rate</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>GST%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dbeafe', width: '12%', background: 'transparent', color: 'white', fontWeight: 600 }}>Taxable</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dbeafe', width: '12%', background: 'transparent', color: 'white', fontWeight: 600 }}>Total</th>
                  {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '6%', background: 'transparent', color: 'white', fontWeight: 600 }}></th>}
                </tr>
              </thead>
              <tbody>
                {transferDetails.map((d, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? (isViewMode ? '#f1f5f9' : '#eff6ff') : '#ffffff' }}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: 600, color: isViewMode ? '#475569' : '#2563eb' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', fontWeight: 600 }}>
                      <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'}</span>
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: 600, color: '#6b7280' }}>
                      {d.HSNCode || '-'}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: 600 }}>
                      {(() => {
                        const availableBalance = getAvailableBalance(d.ItemId, index);
                        const color = availableBalance <= 0 ? '#dc2626' : availableBalance <= 10 ? '#f59e0b' : '#059669';
                        return <span style={{ color, fontSize: '0.75rem' }}>{availableBalance}</span>;
                      })()}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{d.Quantity || 0}</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.Quantity ?? ''} 
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                          disabled={isViewMode} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            max: (() => {
                              return getAvailableBalance(d.ItemId, index);
                            })(),
                            style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } 
                          }} 
                          sx={{ width: '100%' }}
                          error={(() => {
                            const availableBalance = getAvailableBalance(d.ItemId, index);
                            return parseFloat(d.Quantity || 0) > availableBalance;
                          })()}
                        />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{d.GSTRate || 0}%</span>
                      ) : (
                        <TextField size="small" type="number" value={d.GSTRate ?? 0} onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: 600, color: '#1e3a8a' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: 600, color: '#2563eb' }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center' }}>
                        <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {transferDetails.length === 0 && (
                  <tr><td colSpan={isViewMode ? 8 : 9} style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: isViewMode ? '#f1f5f9' : '#eff6ff' }}>No items added</td></tr>
                )}
              </tbody>
            </table>
          </Box>
        )}

        {/* Mobile Card View */}
        {isMobile && (
          <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
            {transferDetails.map((d, index) => (
              <Box key={index} sx={{ border: '1px solid #dbeafe', borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: '#eff6ff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.8rem' }}>
                    {items.find(i => i.Id === parseInt(d.ItemId))?.Name || 'Select Item'}
                  </Typography>
                  {!isViewMode && (
                    <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.5, bgcolor: '#eff6ff', color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  )}
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Item</Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'} 
                    disabled 
                    sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', fontWeight: 600, color: '#1e3a8a' } }} 
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>HSN Code</Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={d.HSNCode || '-'} 
                    disabled 
                    sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' } }} 
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Balance Stock</Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={(() => {
                      const availableBalance = getAvailableBalance(d.ItemId, index);
                      return availableBalance;
                    })()} 
                    disabled 
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        py: 0.75, 
                        fontSize: '0.8rem', 
                        fontWeight: 600, 
                        textAlign: 'center',
                        color: (() => {
                          const availableBalance = getAvailableBalance(d.ItemId, index);
                          return availableBalance <= 0 ? '#dc2626' : availableBalance <= 10 ? '#f59e0b' : '#059669';
                        })()
                      } 
                    }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Quantity</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={d.Quantity || ''} 
                      onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      inputProps={{ 
                        min: 0,
                        max: (() => {
                          return getAvailableBalance(d.ItemId, index);
                        })()
                      }} 
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }}
                      error={(() => {
                        const availableBalance = getAvailableBalance(d.ItemId, index);
                        return parseFloat(d.Quantity || 0) > availableBalance;
                      })()}
                      helperText={(() => {
                        const availableBalance = getAvailableBalance(d.ItemId, index);
                        const quantity = parseFloat(d.Quantity || 0);
                        if (quantity > availableBalance) {
                          return `Exceeds balance (${availableBalance})`;
                        }
                        return '';
                      })()}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Rate</Typography>
                    <TextField size="small" type="number" value={d.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>GST %</Typography>
                    <TextField size="small" type="number" value={d.GSTRate || 0} onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0, max: 100 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #dbeafe', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 600 }}>Taxable Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a' }}>{formatCurrency(d.TaxableAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #dbeafe' }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 600 }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(d.TotalAmount)}</Typography>
                </Box>
              </Box>
            ))}
            {transferDetails.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#2563eb', bgcolor: '#eff6ff', borderRadius: 1, border: '1px dashed #dbeafe' }}><Typography variant="body2">No items added</Typography></Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Summary Section */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={8}>
            {/* Transport Information Display */}
            {isViewMode && (formData.TransporterName || formData.TransporterGST || formData.VehicleNumber || formData.Distance) && (
              <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, mb: 1.5, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Transport Information</Typography>
                <Grid container spacing={1}>
                  {formData.TransporterName && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Transporter Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a' }}>{formData.TransporterName}</Typography>
                    </Grid>
                  )}
                  {formData.TransporterGST && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Transporter GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a' }}>{formData.TransporterGST}</Typography>
                    </Grid>
                  )}
                  {formData.VehicleNumber && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Vehicle Number</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a' }}>{formData.VehicleNumber}</Typography>
                    </Grid>
                  )}
                  {formData.Distance && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Distance</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a' }}>{formData.Distance} KM</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
            
            {/* Audit Info */}
            {isViewMode && (
              <Box sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                  Created by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#dc2626' }}>{formData.CreatedByName || '-'}</Typography> on{' '}
                  <Typography component="span" variant="caption" sx={{ color: '#b91c1c' }}>
                    {formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                  </Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                  Modified by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#b91c1c' }}>{formData.ModifiedByName || '-'}</Typography> on{' '}
                  <Typography component="span" variant="caption" sx={{ color: '#991b1b' }}>
                    {formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.ModifiedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                  </Typography>
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#2563eb', mb: 1, display: 'block' }}>Summary</Typography>
            <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#eff6ff', p: 1.5, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Total Quantity</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{formData.TotalQuantity || 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Taxable Amount</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(formData.TotalTaxableAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">CGST Amount</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(formData.TotalCGSTAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">SGST Amount</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(formData.TotalSGSTAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Total with GST</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(formData.TotalWithGST)}</Typography>
              </Box>
              {!isViewMode && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption">Round Off</Typography>
                  {/* <TextField
                    size="small"
                    type="number"
                    value={formData.RoundOff || 0}
                    onChange={(e) => {
                      const roundOffValue = parseFloat(e.target.value) || 0;
                      const newGrandTotal = Math.round((formData.TotalWithGST || 0) + roundOffValue);
                      setFormData(prev => ({ 
                        ...prev, 
                        RoundOff: roundOffValue,
                        GrandTotal: newGrandTotal
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    inputProps={{ 
                      step: 0.01,
                      style: { textAlign: 'right', padding: '2px 4px', fontSize: '0.7rem' } 
                    }}
                    sx={{ 
                      width: '80px',
                      '& .MuiOutlinedInput-root': { height: '24px' }
                    }}
                  /> */}

                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(formData.RoundOff)}</Typography>
                </Box>
              )}
              {isViewMode && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">Round Off</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>
                    {formatCurrency(formData.RoundOff || 0)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Grand Total</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563eb' }}>₹{(formData.GrandTotal || 0).toLocaleString('en-IN')}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe', bgcolor: isViewMode ? '#f8fafc' : '#eff6ff' }}>
        <Button onClick={handleCancelClick} variant="outlined" size="small">Cancel</Button>
        {!isViewMode && <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}>Save</Button>}
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog 
        open={itemsModalOpen} 
        onClose={handleViewAllItemsClose} 
        maxWidth="lg"
        fullWidth 
        PaperProps={{ 
          sx: { 
            borderRadius: '12px', 
            maxHeight: '90vh', 
            position: 'relative', 
            m: 1
          } 
        }}
      >
        <DialogTitle sx={{ py: 1, px: 1.5, borderBottom: '1px solid #dbeafe', bgcolor: '#eff6ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 sx={{ color: '#2563eb', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.9rem' }}>All Transfer Items ({transferDetails.length})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={handleViewAllItemsClose} size="small" sx={{ p: 0.4 }}><Close fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}>#</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: 'white', fontWeight: 600, fontSize: '0.7rem', minWidth: '150px' }}>Item Name</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '100px' }}>HSN Code</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>Balance</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>Quantity</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>Rate</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '70px' }}>GST%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '100px' }}>Taxable</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '100px' }}>Total</th>
                  {!isViewMode && <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '50px' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {transferDetails.map((d, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#eff6ff' : '#ffffff', borderBottom: '1px solid #dbeafe' }}>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 500, color: '#2563eb', fontSize: '0.75rem' }}>{index + 1}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500, color: '#1e3a8a' }}>
                      <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'}</span>
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem' }}>
                      {d.HSNCode || '-'}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600 }}>
                      {(() => {
                        const availableBalance = getAvailableBalance(d.ItemId, index);
                        const color = availableBalance <= 0 ? '#dc2626' : availableBalance <= 10 ? '#f59e0b' : '#059669';
                        return <span style={{ color, fontSize: '0.75rem' }}>{availableBalance}</span>;
                      })()}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#1e3a8a' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{d.Quantity || 0}</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.Quantity ?? ''} 
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            max: (() => {
                              return getAvailableBalance(d.ItemId, index);
                            })(),
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }} 
                          sx={{ 
                            width: '70px', 
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                          error={(() => {
                            const availableBalance = getAvailableBalance(d.ItemId, index);
                            return parseFloat(d.Quantity || 0) > availableBalance;
                          })()}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#1e3a8a' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.Rate ?? ''} 
                          onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            style: { textAlign: 'right', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }} 
                          sx={{ 
                            width: '80px', 
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }} 
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#1e3a8a' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{d.GSTRate || 0}%</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.GSTRate ?? 0} 
                          onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            max: 100,
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }} 
                          sx={{ 
                            width: '60px', 
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }} 
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#1e3a8a', fontSize: '0.75rem' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => removeDetailRow(index)} 
                          sx={{ p: 0.2, color: '#dc2626', '&:hover': { bgcolor: '#eff6ff' } }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {transferDetails.length === 0 && (
                  <tr>
                    <td colSpan={isViewMode ? 9 : 10} style={{ padding: '16px', textAlign: 'center', color: '#6b7280', backgroundColor: '#eff6ff', fontSize: '0.8rem' }}>
                      No items added
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot style={{ position: 'sticky', bottom: 0, background: 'linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)' }}>
                <tr style={{ borderTop: '2px solid #2563eb' }}>
                  <td colSpan="4" style={{ padding: '8px 6px', fontWeight: 700, textAlign: 'right', color: '#1e3a8a', fontSize: '0.75rem' }}>Totals:</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{transferDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0)}</td>
                  <td colSpan="2"></td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{formatCurrency(transferDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{formatCurrency(transferDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0))}</td>
                  {!isViewMode && <td style={{ padding: '8px 6px' }}></td>}
                </tr>
              </tfoot>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #dbeafe', bgcolor: '#eff6ff', justifyContent: 'flex-end' }}>
          <Button onClick={handleViewAllItemsClose} variant="outlined" size="small" sx={{ fontSize: '0.75rem', py: 0.4, px: 1.5 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{
              borderColor: '#dc2626',
              color: '#dc2626',
              '&:hover': { borderColor: '#b91c1c', backgroundColor: '#fef2f2' }
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal - Performance Optimized */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', maxHeight: '70vh' } }}
      >
        <DialogTitle sx={{ py: 1, px: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#1e40af', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                Select Items for Transfer
              </Typography>
              <Chip 
                label={`${selectedItems.length} Selected`} 
                color="primary" 
                size="small" 
                sx={{ fontSize: '0.65rem', height: '18px' }} 
              />
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {/* Search and Selected Items */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f9fafb' }}>
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search items by name, HSN code, or description..."
              value={getItemsSearchValue}
              onChange={(e) => setGetItemsSearchValue(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: '#6b7280', mr: 1 }} />,
                endAdornment: getItemsLoading && <CircularProgress size={20} />
              }}
              sx={{ mb: 1.5 }}
            />

            {/* Selected Items Display */}
            {selectedItems.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', mb: 1, display: 'block' }}>
                  Selected Items ({selectedItems.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 60, overflow: 'auto' }}>
                  {selectedItems.map(item => (
                    <Chip
                      key={item.Id}
                      label={`${item.Name} (${item.selectedQuantity})`}
                      size="small"
                      onDelete={() => removeSelectedItem(item.Id)}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Items List */}
          <Box sx={{ maxHeight: '35vh', overflow: 'auto' }}>
            {getPaginatedItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
                <Typography>No items found</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 0.5 }}>
                {/* Bulk Select */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Checkbox
                    checked={getPaginatedItems.length > 0 && getPaginatedItems.every(item => 
                      selectedItems.some(si => si.Id === item.Id) || isItemAlreadyInTransfer(item.Id)
                    )}
                    indeterminate={getPaginatedItems.some(item => 
                      selectedItems.some(si => si.Id === item.Id)
                    ) && !getPaginatedItems.every(item => 
                      selectedItems.some(si => si.Id === item.Id) || isItemAlreadyInTransfer(item.Id)
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        getPaginatedItems.forEach(item => {
                          if (!selectedItems.find(si => si.Id === item.Id) && !isItemAlreadyInTransfer(item.Id)) {
                            handleItemSelection(item, true);
                          }
                        });
                      } else {
                        getPaginatedItems.forEach(item => {
                          handleItemSelection(item, false);
                        });
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Select All on Page
                  </Typography>
                </Box>

                {/* Items Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '5%' }}>Select</th>
                      <th style={{ padding: '4px', textAlign: 'left', fontWeight: 600, color: '#374151', width: '30%' }}>Item Name</th>
                      <th style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '12%' }}>HSN Code</th>
                      <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '12%' }}>Rate</th>
                      <th style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '12%' }}>Balance Stock</th>
                      <th style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#374151', width: '14%' }}>Quantity</th>
                      <th style={{ padding: '4px', textAlign: 'right', fontWeight: 600, color: '#374151', width: '15%' }}>Custom Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedItems.map((item, index) => {
                      const isSelected = selectedItems.some(si => si.Id === item.Id);
                      const isAlreadyAdded = isItemAlreadyInTransfer(item.Id);
                      const selectedItem = selectedItems.find(si => si.Id === item.Id);

                      return (
                        <tr key={item.Id} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '4px', textAlign: 'center' }}>
                            <Checkbox
                              checked={isSelected}
                              disabled={isAlreadyAdded || getAvailableBalance(item.Id) <= 0}
                              onChange={(e) => handleItemSelection(item, e.target.checked)}
                            />
                          </td>
                          <td style={{ padding: '4px', fontWeight: 500 }}>
                            {item.Name}
                            {isAlreadyAdded && <span style={{ color: '#dc2626', fontSize: '0.7rem', marginLeft: '8px' }}>(Already Added)</span>}
                            {!isAlreadyAdded && getAvailableBalance(item.Id) <= 0 && <span style={{ color: '#dc2626', fontSize: '0.7rem', marginLeft: '8px' }}>(Out of Stock)</span>}
                          </td>
                          <td style={{ padding: '4px', textAlign: 'center' }}>{item.HSNCode || '-'}</td>
                          <td style={{ padding: '4px', textAlign: 'right' }}>{formatCurrency(item.PurchaseRate || 0)}</td>
                          <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: (() => {
                            const availableBalance = getAvailableBalance(item.Id);
                            return availableBalance <= 0 ? '#dc2626' : availableBalance <= 10 ? '#f59e0b' : '#059669';
                          })() }}>
                            {getAvailableBalance(item.Id)}
                          </td>
                          <td style={{ padding: '4px', textAlign: 'center' }}>
                            {isSelected && (
                              <TextField
                                size="small"
                                type="number"
                                value={selectedItem?.selectedQuantity || 1}
                                onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                inputProps={{ 
                                  min: 1, 
                                  max: (() => {
                                    return getAvailableBalance(item.Id);
                                  })(),
                                  style: { textAlign: 'center', padding: '4px', fontSize: '0.8rem' } 
                                }}
                                sx={{ width: '80px' }}
                                error={selectedItem?.selectedQuantity > getAvailableBalance(item.Id)}
                                helperText={selectedItem?.selectedQuantity > getAvailableBalance(item.Id) ? 'Exceeds balance' : ''}
                              />
                            )}
                          </td>
                          <td style={{ padding: '4px', textAlign: 'right' }}>
                            {isSelected && (
                              <TextField
                                size="small"
                                type="number"
                                value={selectedItem?.selectedRate || item.PurchaseRate || 0}
                                onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.8rem' } }}
                                sx={{ width: '100px' }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            )}
          </Box>

          {/* Pagination */}
          {totalItemsPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderTop: '1px solid #e2e8f0' }}>
              <Pagination
                count={totalItemsPages}
                page={currentItemsPage}
                onChange={(e, page) => setCurrentItemsPage(page)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Total Items: <strong>{filteredItems.length}</strong> | 
            Selected: <strong>{selectedItems.length}</strong>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small" sx={{ borderColor: '#d1d5db', color: '#374151' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddSelectedItems} 
              variant="contained" 
              size="small" 
              disabled={selectedItems.length === 0 || addingItemsLoading}
              startIcon={addingItemsLoading ? <CircularProgress size={16} /> : <Add />}
              sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}
            >
              {addingItemsLoading ? 'Adding...' : `Add ${selectedItems.length} Items`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default StockTransferOutModal;